/**
 * Compliance Alerts Component
 * Displays all compliance items that need attention (missing, expiring, expired)
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  AlertTriangle, XCircle, Clock, CheckCircle2, Upload,
  Calendar, Filter, ArrowUp, ArrowDown
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ComplianceAlerts = ({ alerts = [], categoryConfig, onStatusUpdate }) => {
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortBy, setSortBy] = useState("weight");
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState("");
  const [uploadExpiration, setUploadExpiration] = useState("");

  // Filter and sort alerts
  let filteredAlerts = [...alerts];

  if (filterStatus !== "all") {
    filteredAlerts = filteredAlerts.filter(a => a.status === filterStatus);
  }

  if (filterCategory !== "all") {
    filteredAlerts = filteredAlerts.filter(a => a.category === filterCategory);
  }

  // Sort
  if (sortBy === "weight") {
    filteredAlerts.sort((a, b) => b.weight - a.weight);
  } else if (sortBy === "status") {
    const statusPriority = { expired: 0, expiring: 1, missing: 2 };
    filteredAlerts.sort((a, b) => statusPriority[a.status] - statusPriority[b.status]);
  } else if (sortBy === "category") {
    filteredAlerts.sort((a, b) => a.category.localeCompare(b.category));
  }

  // Handle quick action (upload document)
  const handleQuickUpload = async () => {
    if (!uploadFile || !selectedAlert) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('notes', uploadNotes);
    formData.append('expirationDate', uploadExpiration);

    try {
      await axios.post(`${API}/compliance/status/${selectedAlert.itemId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Document uploaded and status updated!");
      setActionDialogOpen(false);
      setUploadFile(null);
      setUploadNotes("");
      setUploadExpiration("");
      onStatusUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  // Mark as complete without document
  const markComplete = async (alert) => {
    try {
      await axios.put(`${API}/compliance/status/${alert.itemId}`, {
        status: "complete",
        notes: "Marked complete manually"
      });
      toast.success("Item marked as complete");
      onStatusUpdate?.();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'expired': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'expiring': return <Clock className="w-5 h-5 text-amber-600" />;
      default: return <AlertTriangle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'expired': return 'bg-red-100 text-red-800 border-red-300';
      case 'expiring': return 'bg-amber-100 text-amber-800 border-amber-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Count by status
  const expiredCount = alerts.filter(a => a.status === 'expired').length;
  const expiringCount = alerts.filter(a => a.status === 'expiring').length;
  const missingCount = alerts.filter(a => a.status === 'missing').length;

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                <XCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-700">{expiredCount}</p>
                <p className="text-sm text-red-600">Expired Items</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{expiringCount}</p>
                <p className="text-sm text-amber-600">Expiring Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gray-500 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-700">{missingCount}</p>
                <p className="text-sm text-gray-600">Missing Items</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="expiring">Expiring</SelectItem>
                <SelectItem value="missing">Missing</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(categoryConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weight">By Priority</SelectItem>
                <SelectItem value="status">By Status</SelectItem>
                <SelectItem value="category">By Category</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      {filteredAlerts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900">All Clear!</h3>
            <p className="text-muted-foreground mt-2">
              No compliance alerts matching your filters.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredAlerts.map((alert, index) => {
            const config = categoryConfig[alert.category];
            const Icon = config?.icon;

            return (
              <Card 
                key={index}
                className="hover:shadow-md transition-shadow"
                data-testid={`alert-card-${index}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      {/* Status Icon */}
                      <div className="flex-shrink-0">
                        {getStatusIcon(alert.status)}
                      </div>

                      {/* Category Icon */}
                      {Icon && (
                        <div className={`w-10 h-10 ${config.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                      )}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900">{alert.itemName}</h4>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge className={`${getStatusColor(alert.status)} border`}>
                            {alert.status === 'expired' ? 'Expired' : alert.status === 'expiring' ? 'Expiring Soon' : 'Missing'}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {config?.label}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            Weight: {alert.weight}
                          </Badge>
                          {alert.expirationDate && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(alert.expirationDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedAlert(alert);
                          setActionDialogOpen(true);
                        }}
                        className="gap-1"
                      >
                        <Upload className="w-4 h-4" />
                        Upload
                      </Button>
                      {!alert.requiresDocument && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => markComplete(alert)}
                          className="gap-1"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Complete
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Quick Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Alert</DialogTitle>
            <DialogDescription>
              Upload a document for: {selectedAlert?.itemName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="alert-file">Document File</Label>
              <Input
                id="alert-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Supported: PDF, JPG, PNG, WebP (max 15MB)
              </p>
            </div>

            <div>
              <Label htmlFor="alert-expiration">Expiration Date (Optional)</Label>
              <Input
                id="alert-expiration"
                type="date"
                value={uploadExpiration}
                onChange={(e) => setUploadExpiration(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="alert-notes">Notes (Optional)</Label>
              <Textarea
                id="alert-notes"
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                placeholder="Add any relevant notes..."
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleQuickUpload}
              disabled={!uploadFile || uploading}
              className="gap-2"
            >
              {uploading ? "Uploading..." : "Upload & Resolve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComplianceAlerts;
